/**
 * MDMSOrchestratorView - Streaming Orchestrator Dashboard
 * 
 * Real-time view of the Market Data streaming orchestrator status.
 * Fetches data from MDMS API and subscribes to WebSocket for live updates.
 * 
 * @author DeeTrade Team
 * @created 2024-12-27
 * @updated 2024-12-28 - Added demand matrix table and WebSocket subscription
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
    FiCpu, FiActivity, FiServer, FiTerminal, FiRefreshCw, 
    FiCheck, FiX, FiZap, FiGlobe, FiPlay, FiPause, FiStopCircle 
} from 'react-icons/fi';
import { services } from '../../../../services/api';
import useWebSocket from '../../../../hooks/useWebSocket';
import useBreakpoint from '../../../../hooks/useBreakpoint';
import { useAuth } from '../../../../contexts/AuthContext';

const MDMSOrchestratorView = () => {
    // --> Auth
    const { isAuthenticated, loading: authLoading } = useAuth();
    // The demand matrix is 10 columns wide — it becomes cards on a phone.
    const { isPhone } = useBreakpoint();

    // --> State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);
    
    // Streaming status from API
    const [streamingStatus, setStreamingStatus] = useState({
        status: 'idle',
        running: false,
        is_leader: false,
        demand: { flattrade: 0, icici: 0 },
        exchange_status: {},
        stats: {
            reconcile_count: 0,
            last_reconcile: null,
            subscribed_instruments: 0,
            streaming_enabled: false,
            exchanges_open: [],
            errors: 0
        }
    });
    
    // Demand matrix (per-instrument breakdown)
    const [demandMatrix, setDemandMatrix] = useState({
        instruments: [],
        stats: {
            total_streaming: 0,
            total_idle: 0,
            total_demand: 0,
            active_brokers: 0
        }
    });
    

    
    // Command log for terminal view
    const [commandLog, setCommandLog] = useState([]);

    // --> WebSocket for live updates
    const { 
        subscribeTopic, 
        isConnected, 
        connectionState, 
    } = useWebSocket();
    
    useEffect(() => {
        if (!isConnected) return;
        
        // Subscribe to orchestrator status updates via Redis-bridged topic
        const handleOrchestratorUpdate = (message) => {
            // console.log('🔴 Received orchestrator update:', message); // Optimized: Removed full payload log
            console.log('🔴 orchestrator update:', message.event_type || 'unknown');
            
            // Handle message data (could be nested in 'data' or direct)
            const payload = message.data || message;
            
            if (payload?.status) {
                setStreamingStatus(payload.status);
            }
            if (payload?.demand_matrix) {
                setDemandMatrix(prev => ({
                    ...prev,
                    instruments: payload.demand_matrix
                }));
            }
            setLastUpdate(new Date());
            
            // Add to command log
            const eventType = payload?.event_type || message?.event_type;
            if (eventType) {
                // Handle subscribe/unsubscribe action events
                const action = payload?.action || eventType.toUpperCase();
                const broker = payload?.broker || 'MDMS';
                const count = payload?.count || '';
                const displayMessage = payload?.message || `Orchestrator ${eventType}`;
                
                setCommandLog(prev => [{
                    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    time: new Date().toLocaleTimeString(),
                    action: action,
                    message: displayMessage,
                    broker: broker
                }, ...prev.slice(0, 19)]); // Keep last 20
            }

            // Clear any API errors since we are receiving live data
            setError(null);
        };
        
        // Use subscribeTopic for Redis pub/sub bridged messages
        const unsubscribe = subscribeTopic('orchestrator.status', handleOrchestratorUpdate);
        console.log('📡 Subscribed to orchestrator.status topic');
        
        return () => {
            if (unsubscribe && typeof unsubscribe === 'function') {
                unsubscribe();
                console.log('📡 Unsubscribed from orchestrator.status topic');
            }
        };
    }, [isConnected, subscribeTopic]);

    // --> Fetch Status from API
    const fetchStatus = useCallback(async () => {
        try {
            const [statusRes, matrixRes] = await Promise.all([
                services.streaming.getStatus(),
                services.streaming.getDemandMatrix()
            ]);
            
            setStreamingStatus(statusRes.data);
            setDemandMatrix(matrixRes.data);
            setLastUpdate(new Date());
            setError(null);
        } catch (err) {
            console.error('Failed to fetch streaming status:', err);
            setError(err.message || 'Failed to connect to streaming API');
        } finally {
            setLoading(false);
        }
    }, []);

    // --> Auto-refresh (fallback when WebSocket not connected)
    // Guard with auth check to prevent 401 errors on initial mount
    // Also wait for authLoading to be false before making API calls
    useEffect(() => {
        // Don't fetch while auth is still initializing
        if (authLoading) {
            console.log('[MDMSOrchestratorView] Auth still loading, waiting...');
            return;
        }
        
        // Don't fetch if not authenticated
        if (!isAuthenticated) {
            console.log('[MDMSOrchestratorView] Not authenticated, skipping fetch.');
            setLoading(false);
            return;
        }
        
        console.log('[MDMSOrchestratorView] Auth ready and authenticated, fetching status...');
        fetchStatus();
        
        if (autoRefresh && !isConnected) {
            const interval = setInterval(fetchStatus, 5000);
            return () => clearInterval(interval);
        }
    }, [fetchStatus, autoRefresh, isConnected, isAuthenticated, authLoading]);

    // --> Render Helpers
    const StatusBadge = ({ status, isLeader }) => {
        const getStatusColor = () => {
            if (status === 'leader') return 'bg-profit';
            if (status === 'follower') return 'bg-accent';
            if (status === 'running') return 'bg-warning';
            return 'bg-fg-3';
        };
        
        return (
            <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${getStatusColor()} animate-pulse`}></span>
                <span className="font-bold text-lg capitalize text-fg-1">{status}</span>
                {isLeader && status !== 'leader' && (
                    <span className="ds-badge xs profit">
                        LEADER
                    </span>
                )}
            </div>
        );
    };

    const emptyDemandMessage = 'No active streaming demand';

    /* --> Demand Matrix shared cells.
       The table (desktop) and the cards (phone) both render these, so a change to
       how a state or an instrument reads can't apply to only one of them. */
    /* --> #41 — how well-evidenced is a STREAMING badge?
       `per_instrument` = the adapter named THIS instrument. Anything else = a
       broker-wide "something ticked" estimate, which is exactly the signal that
       used to mask a partial subscription loss as green.
       //! Rendered as VISIBLE text, not just a title= tooltip. The whole point of
       //! this field is at-a-glance scanning: a degraded row that looks identical
       //! to a verified one until you hover over it is the hollow-green problem
       //! wearing a different hat. */
    const renderEvidence = (item) => {
        if (!item.streaming_source || item.streaming_source === 'per_instrument') return null;
        const stale = item.streaming_source === 'stale_broker_aggregate';
        return (
            <span
                className={`ds-badge xs sq ${stale ? 'warn' : 'neutral'} ml-1`}
                title={stale
                    ? 'Per-instrument status from this broker is STALE — this row falls back to a broker-wide tick estimate'
                    : 'No per-instrument status from this broker — this row rests on a broker-wide tick estimate'}
            >
                {stale ? 'STALE EST' : 'EST'}
            </span>
        );
    };

    /* --> #41 — the operator's cross-check against the badge. null means "we have
       no per-instrument data", which is NOT the same claim as zero ticks. */
    const renderTicks = (item) => (
        item.ticks_received === null || item.ticks_received === undefined
            ? <span className="text-xs font-mono text-fg-3" title="No per-instrument tick data from this broker">—</span>
            : <span className="text-xs font-mono text-fg-2">{item.ticks_received.toLocaleString()}</span>
    );

    const renderDemandState = (item, alignClass = 'justify-end') => {
        const base = `flex items-center gap-1 text-xs ${alignClass}`;
        if (item.state === 'STREAMING') return (
            <span className={`${base} font-bold text-profit`} title={item.status_detail}>
                <span className="w-2 h-2 rounded-full bg-profit animate-pulse"></span>
                STREAMING
                {renderEvidence(item)}
            </span>
        );
        if (item.state === 'SUBSCRIBED') return (
            <span className={`${base} font-bold text-accent`} title={item.status_detail || 'Waiting for data'}>
                <span className="w-2 h-2 rounded-full bg-accent animate-ping opacity-75"></span>
                SUBSCRIBED
            </span>
        );
        if (item.state === 'WAITING') return (
            <span className={`${base} font-medium text-warning`} title={item.status_detail || 'Exchange closed'}>
                <FiStopCircle size={12} />
                WAITING
            </span>
        );
        if (item.state === 'ERROR') return (
            <span className={`${base} font-bold text-loss transition-colors cursor-help`} title={item.error || 'Subscription failed'}>
                <FiX size={12} />
                ERROR
            </span>
        );
        return (
            <span className={`${base} text-fg-3`} title={item.status_detail || 'Pending'}>
                <FiStopCircle size={12} />
                {item.state || 'IDLE'}
            </span>
        );
    };

    const renderDemandIdentity = (item) => (
        <div className="flex items-center gap-2 min-w-0">
            {item.is_hq && (
                <span className="ds-badge xs sq warn shrink-0" title="High Quality Streaming Enabled">HQ</span>
            )}
            {item.active_symbol ? (
                <div className="flex flex-col min-w-0">
                    <span className="truncate" title={`Feed: ${item.instrument}`}>
                        {item.active_symbol}
                        <span className="ml-1 text-[10px] text-accent font-normal">⟳</span>
                    </span>
                    {item.resolved_to?.days_to_expiry != null && (
                        <span className="text-[10px] text-fg-3">
                            {item.resolved_to.days_to_expiry}d to expiry
                        </span>
                    )}
                </div>
            ) : (
                <span className="truncate">{item.instrument}</span>
            )}
        </div>
    );

    const renderDemandBrokers = (item) => (
        <div className="flex flex-col gap-1">
            {item.brokers && item.brokers.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                    {item.brokers.map((broker, i) => {
                        const isSubscribed = item.subscribed_brokers?.includes(broker);
                        return (
                            <span
                                key={i}
                                className={`ds-badge xs sq ${isSubscribed ? 'profit' : 'neutral'}`}
                                title={isSubscribed ? 'Subscribed' : 'Pending'}
                            >
                                {isSubscribed && <span className="ds-badge-dot"></span>}
                                {broker}
                            </span>
                        );
                    })}
                </div>
            ) : (
                <span className="font-bold text-fg-2">{item.broker || 'Unknown'}</span>
            )}
            {(item.is_hq || item.max_brokers > 1) && (
                <span className={`text-[10px] font-medium flex items-center gap-1 ${item.is_hq ? 'text-warning' : 'text-accent'}`}>
                    <FiZap size={8} />
                    {item.is_hq ? 'HQ Streaming' : 'Multi-Broker'} ({item.subscribed_brokers?.length || 0}/{item.max_brokers} active)
                </span>
            )}
            {item.serving_account_id && (
                <span className="text-[10px] text-fg-3">ID: {item.serving_account_id}</span>
            )}
        </div>
    );

    /* Phone: the matrix is 10 columns and ~1200px wide — reading it meant panning
       past the counts to reach State, which is the column that actually matters.
       One card per instrument instead; State rides up next to the symbol. */
    const renderDemandCards = () => {
        if (!demandMatrix.instruments?.length) {
            return <div className="px-4 py-8 text-center text-fg-3 text-sm">{emptyDemandMessage}</div>;
        }

        return (
            <div className="p-3 space-y-3 max-h-[70vh] overflow-y-auto">
                {demandMatrix.instruments.map((item, idx) => {
                    const sources = [
                        { label: 'Strategies', value: item.sources?.strategies || 0, variant: 'accent' },
                        { label: 'Feeds', value: item.sources?.realtime_feeds || 0, variant: 'info' },
                        { label: 'Manual', value: item.sources?.manual || 0, variant: 'warn' },
                    ];
                    return (
                        <div
                            key={`${item.instrument}-${idx}`}
                            className="rounded-xl border border-[var(--divider)] bg-bg-3/30 p-3 space-y-3"
                        >
                            {/* Identity + state */}
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 font-medium text-fg-1 text-sm">
                                    {renderDemandIdentity(item)}
                                    <span className="text-xs text-fg-3">{item.exchange}</span>
                                </div>
                                <div className="shrink-0">{renderDemandState(item, 'justify-end')}</div>
                            </div>

                            {/* Demand counts */}
                            <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center justify-center w-9 h-9 shrink-0 rounded-full font-bold text-sm ${
                                    item.total_interest > 0 ? 'bg-profit/15 text-profit' : 'bg-bg-3 text-fg-3'
                                }`}>
                                    {item.total_interest}
                                </span>
                                <div className="grid grid-cols-3 gap-1 flex-1 min-w-0">
                                    {sources.map(s => (
                                        <div key={s.label} className="text-center">
                                            <div className="ds-eyebrow text-fg-3 text-[9px]">{s.label}</div>
                                            <span className={s.value > 0
                                                ? `ds-badge xs mono ${s.variant} font-bold`
                                                : 'text-xs font-mono text-fg-3'}>
                                                {s.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Serving + brokers + interval */}
                            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-[var(--divider)]">
                                <div className="min-w-0">
                                    <div className="ds-eyebrow text-fg-3 text-[9px] mb-0.5">Serving User</div>
                                    <div className="text-xs font-mono text-fg-1 truncate">
                                        {item.serving_account ? item.serving_account.split('(')[0].trim() : '-'}
                                    </div>
                                    {item.serving_account && item.serving_account.includes('(') && (
                                        <div className="text-[10px] text-fg-3 truncate">
                                            {item.serving_account.match(/\((.*?)\)/)[1]}
                                        </div>
                                    )}
                                </div>
                                <div className="min-w-0">
                                    <div className="ds-eyebrow text-fg-3 text-[9px] mb-0.5">Interval</div>
                                    <span className="ds-badge sm mono neutral">{item.capture_mode || '1m'}</span>
                                </div>
                                {/* --> #41: same cross-check as the desktop table. The two views
                                     render from the same helpers so a change cannot reach only one. */}
                                <div className="min-w-0">
                                    <div className="ds-eyebrow text-fg-3 text-[9px] mb-0.5">Ticks</div>
                                    {renderTicks(item)}
                                </div>
                                <div className="col-span-2 min-w-0">
                                    <div className="ds-eyebrow text-fg-3 text-[9px] mb-1">Broker &amp; Account</div>
                                    <div className="text-xs font-mono">{renderDemandBrokers(item)}</div>
                                </div>
                                {item.subscribers?.length > 0 && (
                                    <div className="col-span-2 min-w-0">
                                        <div className="ds-eyebrow text-fg-3 text-[9px] mb-1">Subscribers</div>
                                        <div className="flex flex-wrap gap-1">
                                            {item.subscribers.slice(0, 3).map((sub, i) => (
                                                <span key={i} className="ds-badge xs sq mono accent">User {sub}</span>
                                            ))}
                                            {item.subscribers.length > 3 && (
                                                <span className="text-[10px] text-fg-2 self-center">
                                                    +{item.subscribers.length - 3} more
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // --> Loading State
    if (loading && !streamingStatus.status) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin text-accent">
                    <FiRefreshCw size={32} />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* Header with Controls */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                    <h2 className="ds-h2 text-fg-1">Streaming Orchestrator</h2>
                    <p className="text-sm text-fg-2">Real-time market data streaming control plane</p>
                </div>
                <div className="flex items-center gap-4 sm:gap-6 shrink-0">
                    <div className="flex flex-col items-start sm:items-end">
                         <div className="text-xs text-fg-2 font-mono flex items-center gap-2">
                            <span>LOCAL</span>
                            <span className="text-fg-1 font-bold">{currentTime.toLocaleTimeString()}</span>
                         </div>
                         <div className="text-xs text-fg-2 font-mono flex items-center gap-2">
                            <span>IST</span>
                            <span className="text-accent font-bold">{currentTime.toLocaleTimeString('en-US', { timeZone: 'Asia/Kolkata' })}</span>
                         </div>
                    </div>

                    {isConnected && (
                        <span className="ds-badge sm profit" title="WebSocket Connected">
                             <span className="ds-badge-dot pulse"></span>
                             LIVE
                        </span>
                    )}
                </div>
            </div>

            {/* Error Banner */}
            {error && (
                <div className="p-4 bg-loss/10 border border-loss/30 rounded-xl text-loss text-sm">
                    ⚠️ {error}
                </div>
            )}

            {/* Stats Cards Row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-bg-2 p-4 rounded-xl border border-[var(--divider)]">
                    <div className="flex items-center gap-2 mb-2">
                        <FiCpu className="text-accent" size={16} />
                        <span className="text-xs text-fg-2 uppercase">Status</span>
                    </div>
                    <StatusBadge status={streamingStatus.status} isLeader={streamingStatus.is_leader} />
                </div>
                
                <div className="bg-bg-2 p-4 rounded-xl border border-[var(--divider)]">
                    <div className="flex items-center gap-2 mb-2">
                        <FiActivity className="text-accent" size={16} />
                        <span className="text-xs text-fg-2 uppercase">Reconciles</span>
                    </div>
                    <span className="text-2xl font-bold text-fg-1">
                        {streamingStatus.stats?.reconcile_count || 0}
                    </span>
                </div>
                
                <div className="bg-bg-2 p-4 rounded-xl border border-[var(--divider)]">
                    <div className="flex items-center gap-2 mb-2">
                        <FiServer className="text-profit" size={16} />
                        <span className="text-xs text-fg-2 uppercase">Streaming</span>
                    </div>
                    <span className="text-2xl font-bold text-profit">
                        {demandMatrix.stats?.total_streaming || 0}
                    </span>
                </div>
                
                <div className="bg-bg-2 p-4 rounded-xl border border-[var(--divider)]">
                    <div className="flex items-center gap-2 mb-2">
                        <FiZap className="text-accent" size={16} />
                        <span className="text-xs text-fg-2 uppercase">Total Demand</span>
                    </div>
                    <span className="text-2xl font-bold text-accent">
                        {demandMatrix.stats?.total_demand || 0}
                    </span>
                </div>
                
                <div className="bg-bg-2 p-4 rounded-xl border border-[var(--divider)]">
                    <div className="flex items-center gap-2 mb-2">
                        <FiGlobe className="text-accent" size={16} />
                        <span className="text-xs text-fg-2 uppercase">Brokers</span>
                    </div>
                    <span className="text-2xl font-bold text-fg-1">
                        {demandMatrix.stats?.active_brokers || 0}
                    </span>
                </div>
            </div>

            {/* Exchange Status Strip */}
            <div className="flex items-center w-full px-4 py-2 bg-bg-2 border border-[var(--divider)] rounded-lg">
                <div className="flex items-center gap-2 mr-6">
                    <FiGlobe className="text-fg-3" size={14} />
                    <span className="ds-eyebrow text-fg-2">Markets</span>
                </div>
                
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    {Object.entries(streamingStatus.exchange_status || {}).map(([exchange, status]) => {
                        const isOpen = status?.is_open;
                        // Closed exchanges were opacity-50 hover:opacity-100 — with no hover
                        // on touch they stayed at half opacity permanently. The hollow dot +
                        // muted fg-2 already say "closed"; legibility shouldn't ride on a
                        // pointer.
                        return (
                            <div key={exchange} className="flex items-center gap-2 group">
                                <div className="relative flex h-2 w-2">
                                    {isOpen ? (
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-profit"></span>
                                    ) : (
                                        <div className="h-1.5 w-1.5 rounded-full border border-[var(--divider)]"></div>
                                    )}
                                </div>
                                <span className={`text-sm ${isOpen ? 'font-bold text-fg-1' : 'font-medium text-fg-2'}`}>
                                    {exchange}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Demand Matrix — full width table on desktop, cards on phone.
                Everything below the divider is shared by both presentations. */}
            <div className="flex flex-col gap-6">

                {/* Top: Demand Matrix Table */}
                <div className="w-full bg-bg-2 rounded-2xl shadow-sm border border-[var(--divider)] overflow-hidden flex flex-col">
                    <div className="px-4 sm:px-6 py-4 border-b border-[var(--divider)] bg-bg-3/50 dark:bg-bg-2/50 flex justify-between items-center gap-3">
                        <h3 className="font-bold text-fg-1 flex items-center gap-2 min-w-0">
                            <FiCpu className="text-accent shrink-0"/> <span className="truncate">Demand Matrix</span>
                        </h3>
                        <span className={`ds-badge sm shrink-0 ${isConnected ? 'profit' : 'info'}`}>
                            {isConnected ? 'Live' : 'Polling'}
                        </span>
                    </div>
                    {isPhone ? renderDemandCards() : (
                    <div className="overflow-x-auto flex-1 max-h-[400px]">
                        <table className="w-full text-sm text-left">
                            <thead className="text-xs text-fg-2 uppercase bg-bg-3/30 border-b border-[var(--divider)] sticky top-0">
                                <tr>
                                    <th className="px-6 py-3">Instrument</th>
                                    <th className="px-6 py-3 text-center">Strategies</th>
                                    <th className="px-6 py-3 text-center">Feeds</th>
                                    <th className="px-6 py-3 text-center">Manual</th>
                                    <th className="px-6 py-3 text-center">Subscribers</th>
                                    <th className="px-6 py-3 text-center">Total</th>
                                    <th className="px-6 py-3">Serving User</th>
                                    <th className="px-6 py-3">Broker & Account</th>
                                    <th className="px-6 py-3">Interval</th>
                                    {/* --> #41: the operator's cross-check against the STREAMING badge */}
                                    <th className="px-6 py-3 text-center">Ticks</th>
                                    <th className="px-6 py-3 text-right">State</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[var(--divider)]">
                                {demandMatrix.instruments?.length > 0 ? (
                                    demandMatrix.instruments.map((item, idx) => (
                                        <tr key={`${item.instrument}-${idx}`} className="hover:bg-bg-3/50 transition-colors">
                                            <td className="px-6 py-3 font-medium text-fg-1">
                                                {renderDemandIdentity(item)}
                                                <span className="ml-2 text-xs text-fg-3">{item.exchange}</span>
                                            </td>
                                            <td className="px-6 py-3 text-center">
                                                <span className={item.sources?.strategies > 0 ? 'ds-badge sm mono accent font-bold' : 'px-2 py-0.5 text-xs font-mono text-fg-3'}>
                                                    {item.sources?.strategies || 0}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3 text-center">
                                                <span className={item.sources?.realtime_feeds > 0 ? 'ds-badge sm mono info font-bold' : 'px-2 py-0.5 text-xs font-mono text-fg-3'}>
                                                    {item.sources?.realtime_feeds || 0}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3 text-center">
                                                <span className={item.sources?.manual > 0 ? 'ds-badge sm mono warn font-bold' : 'px-2 py-0.5 text-xs font-mono text-fg-3'}>
                                                    {item.sources?.manual || 0}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3 text-center">
                                                {item.subscribers?.length > 0 ? (
                                                    <div className="flex flex-col gap-1 items-center">
                                                        {item.subscribers.slice(0, 3).map((sub, i) => (
                                                            <span key={i} className="ds-badge xs sq mono accent">
                                                                User {sub}
                                                            </span>
                                                        ))}
                                                        {item.subscribers.length > 3 && (
                                                            <span className="text-[10px] text-fg-2">+{item.subscribers.length - 3} more</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-fg-3">-</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-3 text-center">
                                                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-xs ${
                                                    item.total_interest > 0
                                                    ? 'bg-profit/15 text-profit'
                                                    : 'bg-bg-3 text-fg-3'
                                                }`}>
                                                    {item.total_interest}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3 text-xs font-mono">
                                                <div className="flex flex-col">
                                                    <span className="text-fg-1 font-medium">
                                                        {item.serving_account ? item.serving_account.split('(')[0].trim() : '-'}
                                                    </span>
                                                    <span className="text-fg-3 text-[10px]">
                                                        {item.serving_account && item.serving_account.includes('(') 
                                                            ? item.serving_account.match(/\((.*?)\)/)[1] 
                                                            : ''}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-3 text-xs font-mono">
                                                {renderDemandBrokers(item)}
                                            </td>
                                            <td className="px-6 py-3">
                                                <span className="ds-badge sm mono neutral">
                                                    {item.capture_mode || '1m'}
                                                </span>
                                            </td>
                                            {/* --> #41: per-instrument tick count. "—" means UNKNOWN
                                                 (no per-instrument data), which is a different claim
                                                 from zero — do not render it as 0. */}
                                            <td className="px-6 py-3 text-center">
                                                {renderTicks(item)}
                                            </td>
                                            <td className="px-6 py-3 text-right">
                                                {renderDemandState(item)}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="11" className="px-6 py-8 text-center text-fg-3">
                                            {emptyDemandMessage}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    )}
                </div>

                {/* Bottom: Command Log — a console by TYPOGRAPHY (mono, uppercase actions,
                    fixed-width time gutter), not by hardcoding a dark slab. It was
                    bg-gray-900 (#111827, blue-tinted) which read as a foreign panel even in
                    dark theme, because the DS dark surface is neutral graphite. Surface,
                    text and the action colour-coding are all tokens now, so it follows the
                    theme in both directions. */}
                <div className="w-full bg-bg-2 text-fg-2 rounded-2xl shadow-sm overflow-hidden flex flex-col font-mono text-sm border border-[var(--divider)]">
                    <div className="px-4 py-3 bg-bg-3/50 dark:bg-bg-2/50 border-b border-[var(--divider)] flex justify-between items-center">
                        <h3 className="flex items-center gap-2 font-semibold text-fg-1">
                            <FiTerminal className="text-profit" />
                            Event Log
                        </h3>

                    </div>
                    <div className="p-4 space-y-2 overflow-y-auto max-h-[350px] flex-1">
                        {commandLog.length > 0 ? (
                            commandLog.map((log) => (
                                <div key={log.id} className="flex gap-2 sm:gap-3 text-xs font-mono border-b border-[var(--divider)] pb-1 last:border-0 hover:bg-bg-3/50 px-2 rounded transition-colors items-start group">
                                    <span className="text-fg-3 min-w-[62px] sm:min-w-[70px] shrink-0 select-none">{log.time}</span>
                                    <span className={`font-bold uppercase tracking-wider min-w-[76px] sm:min-w-[100px] shrink-0 ${
                                        (log.action.includes('SUBSCRIBE') && !log.action.includes('UN')) ? 'text-profit' :
                                        log.action.includes('UNSUBSCRIBE') ? 'text-warning' :
                                        log.action.includes('RECONCILE') ? 'text-accent' :
                                        log.action.includes('ERROR') ? 'text-loss' : 'text-fg-3'
                                    }`}>
                                        {log.action.replace('_COMPLETE', '').replace('_', ' ')}
                                    </span>
                                    <span className="text-fg-2 flex-1 min-w-0 break-words leading-tight">{log.message}</span>
                                </div>
                            ))
                        ) : (
                            <div className="text-fg-3 text-xs italic px-2 pt-2">Waiting for streaming events...</div>
                        )}

                    </div>
                </div>
            </div>


        </div>
    );
};

export default MDMSOrchestratorView;
